import { inject, Injectable, signal } from '@angular/core';
import { ToastService } from 'src/app/services/toast.service';
import { APIService } from '../APIService/api.service';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import { lineString } from '@turf/helpers';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { round } from 'src/app/misc/functions';

@Injectable()
export class GeoAutofill {
  readonly #apiService = inject(APIService);
  readonly #toastr = inject(ToastService);
  readonly #mrTranslate = inject(MrTranslatePipe);

  readonly #OSTAMMID = signal<number>(undefined);

  public registerOSTAMMID = rxMethod(
    tapResponse({ next: (id: number) => this.#OSTAMMID.set(id), error: console.error })
  );
  readonly #geoResource = this.#apiService.getGeoForPosition(this.#OSTAMMID);

  /**
   * Calculates the snapped length on the line.
   * Returns a Promise containing the totalDistance length, or null if it fails.
   */
  public async getSnappedLength(): Promise<number | null> {
    const geo = this.#geoResource.value();

    if (!geo?.length) {
      this.#toastr.warning(this.#mrTranslate.transform('Geodaten konnten nicht geladen werden'));
      return null;
    }

    return new Promise(resolve => {
      const onError: PositionErrorCallback = er => {
        console.error('geolocation.getCurrentPosition: ', er);
        this.#toastr.error(er.message, this.#mrTranslate.transform('Geodaten konnten nicht übernommen werden'));
        resolve(null);
      };
      const onSuccess: PositionCallback = pos => {
        if (!pos) {
          resolve(null);
          return;
        }

        const snapped = nearestPointOnLine(lineString(geo), [pos.coords.longitude, pos.coords.latitude], {
          units: 'meters',
        });

        if (snapped.properties.pointDistance > 50) {
          this.#toastr.warning(
            `${this.#mrTranslate.transform(
              'Sie sind mehr als 50m vom ausgewählten Objekt entfernt - Distanz ist'
            )} ${snapped.properties.pointDistance.toFixed(2)} m`,
            this.#mrTranslate.transform('Geodaten konnten nicht übernommen werden')
          );
          resolve(null);
          return;
        }

        // Return the calculated track/line length back to the caller
        resolve(snapped.properties.totalDistance);
      };
      navigator.geolocation.getCurrentPosition(onSuccess, onError, {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 15000,
      });
    });
  }
}

export function calcPosition({
  snapped,
  start,
  end,
  direction,
}: {
  snapped: number;
  start: number;
  end: number;
  direction: 1 | -1;
}): number {
  if (snapped > (end - start) * direction) return end;
  const value = start + snapped * direction;
  return round(value);
}
